import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdDomain, MdDescription, MdPerson, MdError } from 'react-icons/md';
import type { IconType } from 'react-icons';
import clsx from 'clsx';
import { Link } from '@tanstack/react-router';
import useFetchCompanyCount from '../api/useFetchCompanyCount';
import useFetchProvidedJobPostCount from '../api/useFetchProvidedJobPostCount';
import useFetchCandidateCount from '../api/useFetchCandidateCount';

type KpiCardProps = {
  label: string;
  unit: string;
  value: number | undefined;
  isLoading: boolean;
  Icon: IconType;
  /** ヘルプアイコンのホバー時に表示するツールチップ本文（行ごとに配列） */
  tooltip: string[];
  /** クリック時の遷移先（各管理ページ）。指定するとカード全体がリンクになり hover でグレーになる */
  to?: string;
};

const TOOLTIP_VIEWPORT_PADDING = 8;
/** box と矢印の合計分、トリガー上端からどれだけ浮かせるか（mb-xxs 相当のすき間） */
const TOOLTIP_GAP = 4;

/**
 * ヘルプアイコンのホバーで Figma 準拠のツールチップを上方向に表示する。
 * ヘッダーやスクロール領域の overflow / z-index に被らないよう React Portal で body に描画し、
 * トリガーの位置を getBoundingClientRect で計測して fixed 配置する。
 */
function HelpTooltip({ lines, label }: { lines: string[]; label: string }) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; center: number } | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos(null);
    setAnchor({ top: rect.top, center: rect.left + rect.width / 2 });
  };
  const hide = () => {
    setAnchor(null);
    setPos(null);
  };

  useLayoutEffect(() => {
    if (!anchor || !containerRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = containerRef.current;
    const desiredLeft = anchor.center - w / 2;
    const maxLeft = window.innerWidth - w - TOOLTIP_VIEWPORT_PADDING;
    setPos({
      left: Math.max(TOOLTIP_VIEWPORT_PADDING, Math.min(desiredLeft, maxLeft)),
      top: anchor.top - h - TOOLTIP_GAP,
    });
  }, [anchor]);

  return (
    <span className="relative z-10 inline-flex shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-label={`${label}についての説明を表示`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex shrink-0 cursor-help rounded-full bg-transparent p-0 text-main focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
      >
        <MdError size={12} aria-hidden="true" />
      </button>
      {anchor &&
        createPortal(
          <div
            ref={containerRef}
            role="tooltip"
            className="pointer-events-none fixed z-[200] flex flex-col items-center"
            style={{
              left: pos?.left ?? 0,
              top: pos?.top ?? 0,
              visibility: pos ? 'visible' : 'hidden',
              filter: 'drop-shadow(0 0 6px rgba(0,0,0,0.2))',
            }}
          >
            {/* 黒 box: bg-black / p-8 / rounded-2 / 12px white regular leading-1.7 / 左揃え */}
            <span className="flex flex-col items-start justify-center rounded-xs bg-black p-xs leading-[0] whitespace-nowrap text-white">
              {lines.map((line, i) => (
                <span key={i} className="text-xs leading-[1.7] font-normal text-white">
                  {line}
                </span>
              ))}
            </span>
            {/* 下向き矢印 ▼ (Figma 12x8) — box 直下に隣接 (gap 0) */}
            <span
              aria-hidden="true"
              style={{
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '8px solid #000000',
              }}
            />
          </div>,
          document.body
        )}
    </span>
  );
}

function KpiCard({ label, unit, value, isLoading, Icon, tooltip, to }: KpiCardProps) {
  return (
    <div
      className={clsx(
        'relative flex w-full flex-col justify-center gap-xs rounded-sm bg-white px-xl py-sm shadow-[0_0_2px_0_rgba(0,0,0,0.2)]',
        to && 'cursor-pointer transition-colors hover:bg-gray'
      )}
    >
      {/* カード全体を遷移リンクにする stretched-link。ヘルプボタンは z-10 で上に重ねてクリック可能にする */}
      {to && (
        <Link
          to={to as never}
          aria-label={`${label}の管理ページへ移動`}
          className="absolute inset-0 z-0 rounded-sm"
        />
      )}
      {/* ヘッダ: 20px アイコン + ラベル + 12px ヘルプ(?) アイコン + ツールチップ */}
      <div className="flex shrink-0 items-center gap-xxs">
        <Icon size={20} className="shrink-0 text-main" aria-hidden="true" />
        <span className="text-sm leading-normal font-bold whitespace-nowrap text-main">
          {label}
        </span>
        <HelpTooltip lines={tooltip} label={label} />
      </div>

      {/* 数値 + 単位（ローディング中・データなし（0）は「-」を表示） */}
      <div className="flex h-[36px] shrink-0 items-center">
        <p className="leading-[0] font-bold whitespace-nowrap text-main">
          <span className="text-3xl leading-[1.4]">
            {isLoading || !value ? '-' : value.toLocaleString()}
          </span>
          <span className="text-xl leading-[1.4]">{unit}</span>
        </p>
      </div>
    </div>
  );
}

/**
 * ダッシュボード上段の KPI カード 3 枚。
 *
 * - アイコンは Material Symbols
 * - ヘルプアイコンのホバーで Figma 準拠のツールチップを表示
 * - 各 API は 1 ページだけ取得し total_count のみ表示
 * - 担当者 Select の絞り込みは対象外（組織全体の数値を常に表示）
 */
export default function DashboardKpiCards() {
  const { data: companyCount, isLoading: isCompanyLoading } = useFetchCompanyCount();
  const { data: jobPostCount, isLoading: isJobPostLoading } = useFetchProvidedJobPostCount();
  const { data: candidateCount, isLoading: isCandidateLoading } = useFetchCandidateCount();

  return (
    <div className="grid grid-cols-3 gap-md">
      <KpiCard
        label="稼動企業"
        unit="社"
        value={companyCount}
        isLoading={isCompanyLoading}
        Icon={MdDomain}
        tooltip={['募集中の求人、または', '選考進行中の求人を有する企業']}
        to="/agent/companies/"
      />
      <KpiCard
        label="稼動求人"
        unit="件"
        value={jobPostCount}
        isLoading={isJobPostLoading}
        Icon={MdDescription}
        tooltip={['求職者の募集・提案・選考の', 'いずれかが進行している求人']}
        to="/agent/provided_job_posts/"
      />
      <KpiCard
        label="稼動求職者"
        unit="名"
        value={candidateCount}
        isLoading={isCandidateLoading}
        Icon={MdPerson}
        tooltip={['提案中の求人、または', '面談・面接予定のある求職者']}
        to="/agent/candidates/"
      />
    </div>
  );
}
